import { useContext, useMemo, useState } from 'react';
import t from 'react-translate';
import moment from 'moment';
import { useSelector } from 'react-redux';
import prodPathReplace from 'shared/prod-path-rewrite';

import NvModal, { ModalType } from 'shared/components/nv-modal';
import MentoringProgramContext from '../../context';
import { selectGuidesForProfileCompletionOrReviewMilestoneByRole } from 'redux/selectors/mentorship-program-guides';
import { CompletionStatus, MentoringProgramEnrollment, MentoringProgramEnrollmentRole } from 'redux/schemas/models/mentoring-program-enrollments';
import { MentoringProgramGuide } from 'redux/schemas/models/mentoring-program-guides';
import GuidanceSection from './guidance-section';
import { MentoringProgramMatchingType } from 'redux/schemas/models/mentoring-programs';
import { getMentoringProgramAliases } from 'redux/selectors/mentoring-programs';
import useMentoringProgramRole from 'athena/hooks/use-mentoring-program-role';
import { getMentoringProgramEnrollment } from 'redux/selectors/mentoring-program-enrollments';

const MODAL_WIDTH = 440;

type Aliases = {
  menteeAlias: string;
  menteesAlias: string;
  mentorAlias: string;
  mentorsAlias: string;
};

const SETTINGS = {
  [CompletionStatus.IN_PROGRESS]: {
    image: 'remaining-questions',
    label: 'IN_PROGRESS',
  },
  [CompletionStatus.COMPLETED]: {
    image: 'all-done',
    label: 'COMPLETED',
  },
};

const getTimeLeft = (date: string, unit: moment.unitOfTime.Diff = 'day') => moment(date).diff(moment(), unit);

const getTimeUnitForLessThanDay = (date: string, translationBase: any) => {
  const timeUnit = translationBase.TIME_UNIT.HOUR();
  if (getTimeLeft(date, timeUnit) < 1) {
    return translationBase.TIME_UNIT.MINUTE();
  }
  return timeUnit;
};

const getMonthsAndDaysDescription = (
  date: string,
  timeLeft: number,
  translationBase: any,
  label: string,
) => {
  const timeUnit = translationBase.TIME_UNIT.MONTH();
  const daysLeft = timeLeft % 30;
  if (daysLeft > 0) {
    const monthsLeft = Math.floor(timeLeft / 30);
    const daysUnit = translationBase.TIME_UNIT.DAY();
    return translationBase[label].AUTO_MATCHING.MATCH_DATE_UPCOMING_MONTHS_DAYS(
      daysLeft,
      daysUnit,
      monthsLeft,
      timeUnit,
    );
  }
  const updatedTimeLeft = getTimeLeft(date, timeUnit);
  return translationBase[label].AUTO_MATCHING.MATCH_DATE_UPCOMING(updatedTimeLeft, timeUnit);
};

const getUpcomingDateDescription = (
  date: string,
  translationBase: any,
  label: string,
) => {
  let timeLeft = getTimeLeft(date);
  let timeUnit = translationBase.TIME_UNIT.DAY();

  if (timeLeft === 0) {
    timeUnit = getTimeUnitForLessThanDay(date, translationBase);
    timeLeft = getTimeLeft(date, timeUnit);
    return translationBase[label].AUTO_MATCHING.MATCH_DATE_UPCOMING(timeLeft, timeUnit);
  }

  if (timeLeft > 30) {
    return getMonthsAndDaysDescription(date, timeLeft, translationBase, label);
  }

  return translationBase[label].AUTO_MATCHING.MATCH_DATE_UPCOMING(timeLeft, timeUnit);
};

const getParticipantDrivenDescription = (
  isMentee: boolean,
  isMentor: boolean,
  aliases: Aliases,
  translationBase: any,
  label: string,
  hasConnections: boolean,
  role: MentoringProgramEnrollmentRole,
) => {
  if (hasConnections) {
    return translationBase[label].PARTICIPANT_DRIVEN.ALREADY_MATCHED({
      role: role?.name,
      menteeAlias: aliases.menteeAlias,
      mentorAlias: aliases.mentorAlias,
    });
  }

  if (isMentee) {
    return translationBase[label].PARTICIPANT_DRIVEN.MENTEE({ mentorsAlias: aliases.mentorsAlias });
  }
  if (isMentor) {
    return translationBase[label].PARTICIPANT_DRIVEN.MENTOR({ menteesAlias: aliases.menteesAlias });
  }
  return '';
};

const getAutomatedMatchingDescription = (
  matchAnnouncementDate: string,
  translationBase: any,
  label: string,
) => {
  if (!matchAnnouncementDate) {
    return translationBase[label].AUTO_MATCHING.NO_MATCH_DATE();
  }

  const isMatchPast = moment().isAfter(matchAnnouncementDate);
  return isMatchPast
    ? translationBase[label].AUTO_MATCHING.MATCH_DATE_PASSED()
    : getUpcomingDateDescription(matchAnnouncementDate, translationBase, label);
};

const shouldShowGuidance = (
  guidanceEnabled: boolean,
  label: string,
  matchAnnouncementDate: string,
) => {
  if (!guidanceEnabled || label !== 'COMPLETED') {
    return false;
  }
  const isMatchUpcoming = matchAnnouncementDate && moment(matchAnnouncementDate).isAfter(moment());
  return isMatchUpcoming || !matchAnnouncementDate;
};

const addGuidanceToDescription = (
  description: any,
  translationBase: any,
  label: string,
  matchingType: MentoringProgramMatchingType,
) => {
  let guideDescription = null;

  if (matchingType === MentoringProgramMatchingType.PARTICIPANT_DRIVEN) {
    guideDescription = translationBase[label].PARTICIPANT_DRIVEN.GUIDE_DESCRIPTION();
  } else if (matchingType === MentoringProgramMatchingType.AUTOMATED_MATCHING) {
    guideDescription = translationBase[label].AUTO_MATCHING.GUIDE_DESCRIPTION();
  }

  if (!guideDescription) {
    return description;
  }

  return (
    <>
      {description}
      {guideDescription}
    </>
  );
};

const getModalBodyDescription = (
  isMentee: boolean,
  isMentor: boolean,
  matchingType: MentoringProgramMatchingType,
  aliases: Aliases,
  matchAnnouncementDate: string,
  translationBase: any,
  label: string,
  guidanceEnabled: boolean,
  setShowGuidanceSection: (show: boolean) => void,
  hasConnections: boolean,
  role: MentoringProgramEnrollmentRole,
) => {
  let description = null;

  if (matchingType === MentoringProgramMatchingType.PARTICIPANT_DRIVEN) {
    description = getParticipantDrivenDescription(
      isMentee,
      isMentor,
      aliases,
      translationBase,
      label,
      hasConnections,
      role,
    );
  } else if (matchingType === MentoringProgramMatchingType.AUTOMATED_MATCHING) {
    description = getAutomatedMatchingDescription(matchAnnouncementDate, translationBase, label);
  }

  if (shouldShowGuidance(guidanceEnabled, label, matchAnnouncementDate)) {
    setShowGuidanceSection(true);
    description = addGuidanceToDescription(description, translationBase, label, matchingType);
  }

  return description;
};

type RequiredQuestionsModalProps = {
  show: boolean;
  remainingQuestions: number;
  onClose?: () => void;
};

const RequiredQuestionsModal = ({
  show,
  remainingQuestions,
  onClose,
}: RequiredQuestionsModalProps) => {
  const translationBase = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.REMAINING_QUESTIONS_MODAL;
  const status = remainingQuestions ? CompletionStatus.IN_PROGRESS : CompletionStatus.COMPLETED;
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const {
    matchAnnouncementDate,
    mentorshipProgramEnrollment: enrollmentId,
    guidanceEnabled,
    matchingType,
  } = mentoringProgram || {};
  const { image, label } = SETTINGS[status];
  const [showGuidanceSection, setShowGuidanceSection] = useState(false);
  const { isMentee, isMentor } = useMentoringProgramRole();
  const aliases = useSelector((state) => getMentoringProgramAliases(state, mentoringProgram.id));

  const mentoringProgramEnrollment: MentoringProgramEnrollment = useSelector(
    (state) => getMentoringProgramEnrollment(state, enrollmentId)
  );
  const { role, assignedConnections } = mentoringProgramEnrollment || {};
  const hasConnections = assignedConnections?.length > 0;

  const src = prodPathReplace(`images/${image}.svg`);
  const title = translationBase[label].TITLE(remainingQuestions ?? null);
  const description = useMemo(
    () =>
      getModalBodyDescription(
        isMentee,
        isMentor,
        matchingType,
        aliases,
        matchAnnouncementDate,
        translationBase,
        label,
        guidanceEnabled,
        setShowGuidanceSection,
        hasConnections,
        role
      ),
    [
      isMentee,
      isMentor,
      matchingType,
      aliases,
      matchAnnouncementDate,
      translationBase,
      label,
      guidanceEnabled,
      hasConnections,
      role,
    ]
  );
  const guides: MentoringProgramGuide[] = useSelector(
    selectGuidesForProfileCompletionOrReviewMilestoneByRole(role?.name, status)
  );

  return (
    <NvModal
      show={show}
      width={MODAL_WIDTH}
      height='unset'
      headerBgColor='transparent'
      bodyClassName='p-0'
      type={ModalType.FIXED}
      onClose={onClose}
      header=''
      body={(
        <div className='d-flex flex-column align-items-center text-center'>
          <img src={src} alt={title} />
          <div className='w-100 px-6 py-5'>
            <div className='page-title mb-2'>{title}</div>
            <div className='text-large-body'>{description}</div>
            {showGuidanceSection && guides?.length ? (
              <GuidanceSection
                guides={guides}
                className='mt-2 d-flex justify-content-center'
              />
            ) : null}
          </div>
        </div>
      )}
    />
  );
};

export default RequiredQuestionsModal;
